import { RANGS, type Rarete } from "../contract";

/**
 * Les phrases de la page publique d'une carte (`/c/<numero>-<edition>`) et de sa fenetre de signalement (lot 5 du
 * TCG, PR 5). Ecrites pour tout le monde, sobres et courtes. **Rien du serveur n'est affiche tel quel** : un code
 * d'erreur n'a que sa phrase, ecrite ici, en `Record<Code, texte>` pour chaque liste fermee. Le nom d'une carte et sa
 * chronique viennent des donnees et sont rendus comme du texte.
 */

export const ACCUEIL_PATH = "/";

/** Le nom d'une rarete, tel que la carte l'ecrit. */
export function nomRarete(rarete: Rarete): string {
  if (rarete === "chatelain") return "Châtelain";
  return RANGS.find((rang) => rang.id === rarete)?.nom ?? rarete;
}

export const PAGE = {
  retour: "La Taverne",
  chargement: "Chargement de la carte...",
  chronique: "Chronique",
  chroniqueIa: "Chronique écrite par une IA",
  chroniqueEnCours: "Chronique en cours d'écriture",
  statistiques: "Statistiques",
  titreInfos: "Cette carte",
  rarete: "Rareté",
  edition: "Édition",
  tirage: "Tirage",
  habitants: "Habitants qui l'ont",
  aide: "Incline ton téléphone ou glisse le doigt : le reflet suit.",
  partager: "Partager",
  signaler: "Signaler cette carte",
  /** Dit apres « Partager » : l'adresse est dans le presse-papiers. */
  copie: "Adresse copiée.",
  /** Le presse-papiers est refuse : on montre l'adresse pour qu'on la copie a la main. */
  copieImpossible: "Copie impossible. Voici l'adresse à copier :",
} as const;

/** « 3e éd. » devient « 3e édition » : la ligne d'info n'a pas la place d'un pied de carte. */
export function editionEnToutesLettres(edition: string): string {
  return edition.replace(/ éd\.$/, " édition");
}

/** L'etat de l'edition dans la ligne d'info. */
export function etatEdition(enCirculation: boolean): string {
  return enCirculation ? "en circulation" : "archivée";
}

/** Combien d'habitants ont la carte : « Personne ne l'a encore », « 1 habitant l'a », « 18 habitants l'ont ». */
export function habitants(nombre: number): string {
  if (nombre <= 0) return "Personne ne l'a encore";
  if (nombre === 1) return "1 habitant l'a";
  return `${nombre} habitants l'ont`;
}

/** Le tirage d'une edition limitee : « 3 / 10 sortis ». */
export function tirage(exemplaires: number, tirageMax: number): string {
  return `${exemplaires} / ${tirageMax} sortis`;
}

/** Le titre de l'onglet. */
export function titreDeLOnglet(nom: string, rarete: Rarete): string {
  return `${nom}, ${nomRarete(rarete)} · La Taverne`;
}

/** Le texte alternatif de l'image : ce que la carte dit, pour qui ne la voit pas. */
export function texteAlternatif(args: {
  nom: string;
  rarete: Rarete;
  edition: string;
  chronique: string | null;
}): string {
  const base = `Carte de ${args.nom}, ${nomRarete(args.rarete)}, ${args.edition}.`;
  return args.chronique === null ? base : `${base} ${args.chronique}`;
}

export const INCONNUE = {
  titre: "Cette carte n'existe pas ou a été retirée.",
  texte: "L'adresse est peut-être mal recopiée.",
  lien: "Retour à La Taverne",
} as const;

export const ERREUR = {
  lecture: "La carte n'a pas pu être lue. Réessaie dans un instant.",
  relire: "Réessayer",
} as const;

export const SIGNALEMENT = {
  titre: "Signaler cette carte",
  intro:
    "Un message déplacé, une chronique qui dérape ? Nicolas est prévenu sans savoir qui a signalé, et peut retirer la carte de tous les albums.",
  pourquoi: "Pourquoi ?",
  envoyer: "Envoyer le signalement",
  envoi: "Envoi...",
  annuler: "Annuler",
  fermer: "Fermer",
  merci: "Merci. Nicolas est prévenu, sans savoir qui a signalé.",
} as const;

export type MotifCode = "blessante" | "privee" | "autre";

/** Les trois motifs, dans l'ordre de la fenetre. */
export const MOTIFS: readonly MotifCode[] = ["blessante", "privee", "autre"];

export const LIBELLES_MOTIFS: Record<MotifCode, string> = {
  blessante: "La chronique est blessante",
  privee: "Elle dévoile quelque chose de privé",
  autre: "Autre chose",
};

/** Ce que dit la fenetre quand le signalement ne part pas : un code connu, sinon la phrase generale. */
export const ERREURS_SIGNALEMENT = {
  trop_de_signalements: "Trop de signalements en peu de temps. Réessaie plus tard.",
  carte_inconnue: "Cette carte n'existe plus : rien à signaler.",
  autre: "Le signalement n'a pas pu partir. Réessaie dans un instant.",
  incertain: "Le signalement est peut-être parti. Tu peux fermer cette fenêtre.",
} as const;

export type CodeErreurSignalement = keyof typeof ERREURS_SIGNALEMENT;
